import { useState, useEffect } from 'react';
import { useMutation, useQuery } from "@tanstack/react-query";
import { GetAnnouncements } from "../../api/Announcement/GetAllAnnouncements";
import Selectbox, { option } from '../../components/Shared/SelectBox';
import { GetPinnedAnnouncement } from '../../api/Announcement/GetPinnedAnnoucement';
import Input from '../../components/Shared/Input';
import Button from '../../components/Shared/Button';
import { AddNewAnnouncement } from '../../api/Announcement/AddAnnouncement';
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from 'react-toastify';
import PinIcon from '../../components/Icons/PinIcon';
import { PinOffIcon } from 'lucide-react';
import { queryClient } from '../../main';
import { EditAnnouncement } from '../../api/Announcement/EditAnnouncement';
import DeleteModal from '../../components/Shared/DeleteModal';
import { DeleteAnnouncement } from '../../api/Announcement/DeleteAnnouncement';
import TrashIcon from '../../components/Icons/TrashIcon';
import Spinner from '../../components/Shared/Spinner';

const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    message: Yup.string().required("Message is required"),
});

const Announcement = () => {
    const [selectedAnnouncement, setSelectedAnnouncement] = useState<option | null>(null);
    const [countdown, setCountdown] = useState<{ [key: string]: { days: number, hours: number, minutes: number, seconds: number } }>({});

    const calculateEndDate = (createdAt: string | number | Date, deadline: string) => {
        if (!deadline) {
            // Handle cases where deadline is not provided
            return "";
        }

        const [hours, minutes, seconds] = deadline.split(":").map(Number);

        if (
            isNaN(hours) || isNaN(minutes) || isNaN(seconds) ||
            hours < 0 || minutes < 0 || seconds < 0
        ) {
            // Handle invalid deadline format
            console.error("Invalid deadline format:", deadline);
            return "";
        }

        const createdTime = new Date(createdAt).getTime();
        const deadlineInMilliseconds =
            (hours * 60 * 60 * 1000) +
            (minutes * 60 * 1000) +
            (seconds * 1000);

        const endDate = new Date(createdTime + deadlineInMilliseconds);

        return endDate.toISOString();
    };

    const { mutate } = useMutation({
        mutationFn: AddNewAnnouncement,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["announcements"] });
            toast.success('Announcement Added');
            formik.setSubmitting(true);
            formik.resetForm();
        },
        onError: () => {
            formik.setSubmitting(false);
        },
    });

    const { mutate: mutatePinned } = useMutation({
        mutationFn: ({ isPin, id }: { isPin: boolean, id: string }) => EditAnnouncement({ isPin }, id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["announcements"] });
            queryClient.invalidateQueries({ queryKey: ["pinnedAnnouncements"] });
            toast.success("Announcement Updated Successfully");
        },
        onError: (error) => {
            toast.error("Failed to update the announcement");
            console.error(error);
        },
    });

    const { data: AnnouncementsResponse, isLoading: isAnnouncementLoading } = useQuery({
        queryFn: GetAnnouncements,
        queryKey: ["announcements"],
        staleTime: 0,
        refetchInterval: 0,
    });

    const { data: PinnedAnnouncementsResponse, isLoading: isPinnedAnnouncementLoading } = useQuery({
        queryFn: GetPinnedAnnouncement,
        queryKey: ["pinnedAnnouncements"],
        staleTime: 0,
        refetchInterval: 0,
    });

    const announcements = AnnouncementsResponse?.data?.AnnouncementData || [];
    const announcementOptions = announcements.map(announcement => ({
        id: announcement.id,
        name: announcement.title,
        code: announcement.message
    }));

    const pinnedAnnouncements = PinnedAnnouncementsResponse?.data?.AnnouncementData || [];
    const pinnedAnnouncementOptions = pinnedAnnouncements.map(announcement => {
        const endDateISO = calculateEndDate(announcement.createdAt, announcement.deadline);
        return {
            id: announcement.id,
            name: announcement.title,
            code: announcement.message,
            endDate: endDateISO,
            forward_link: announcement.forward_link,
            forward_text: announcement.forward_text
        };
    });

    const formik = useFormik({
        initialValues: {
            title: "",
            message: "",
            deadline: "",
            forward_text: "",
            forward_link: "",
        },
        validationSchema,
        onSubmit: (values) => {
            mutate(values);
        },
    });

    const handlePinAnnouncement = () => {
        if (selectedAnnouncement) {
            mutatePinned({ isPin: true, id: selectedAnnouncement.id! });
        }
    };

    const handleUnpinAnnouncement = (id: string) => {
        mutatePinned({ isPin: false, id });
    };

    const handleDelete = async (id: string) => {
        await DeleteAnnouncement(id);
        setSelectedAnnouncement(null)
        toast.success("Announcement deleted successfully");
    };
    const [isOpen, setIsOpen] = useState(false);

    const calculateTimeLeft = (createdAt: string, deadline: string) => {
        // Convert createdAt to a Date object
        const createdTime = new Date(createdAt).getTime();

        // Parse the deadline (HH:MM:SS) into hours, minutes, and seconds
        const [hours, minutes, seconds] = deadline.split(":").map(Number);

        // Convert the deadline into milliseconds
        const deadlineInMilliseconds =
            (hours * 60 * 60 * 1000) +
            (minutes * 60 * 1000) +
            (seconds * 1000);

        // Calculate the actual deadline time by adding the deadline milliseconds to createdAt time
        const actualDeadlineTime = createdTime + deadlineInMilliseconds;

        // Get the current time
        const currentTime = new Date().getTime();

        // Calculate the difference between the deadline and the current time
        const difference = actualDeadlineTime - currentTime;

        // If the time has passed, return zeros
        if (difference <= 0) {
            return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }

        // Return the calculated time left in days, hours, minutes, and seconds
        return {
            days: Math.floor(difference / (1000 * 60 * 60 * 24)),
            hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
            minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
            seconds: Math.floor((difference % (1000 * 60)) / 1000),
        };
    };

    useEffect(() => {
        const intervals: { [key: string]: NodeJS.Timeout } = {};

        pinnedAnnouncements.forEach((announcement) => {
            if (announcement.deadline && announcement.createdAt) {
                intervals[announcement.id] = setInterval(() => {
                    setCountdown((prev) => ({
                        ...prev,
                        [announcement.id]: calculateTimeLeft(announcement.createdAt, announcement.deadline),
                    }));
                }, 1000);
            }
        });

        return () => {
            Object.values(intervals).forEach(clearInterval);
        };
    }, [pinnedAnnouncements]);

    if (isAnnouncementLoading || isPinnedAnnouncementLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Spinner />
            </div>
        );
    }

    return (
        <div className="py-7">
            <DeleteModal
                queryKey="announcements"
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                mutationFn={handleDelete}
                itemId={selectedAnnouncement?.id || ''}
            />

            <h1 className="text-burntBlack font-bold text-[32px] mb-7">Announcements</h1>
            <div className='mb-8 p-8 rounded-xl bg-white border-black/15 shadow'>
                <h1 className='font-bold text-2xl mb-8'>Current Announcement</h1>
                <div>
                    {pinnedAnnouncementOptions.map(announcement => (
                        <div className='flex items-start justify-between' key={announcement.id}>
                            <div className="mb-4">
                                <h2 className="text-lg font-semibold">{announcement.name}</h2>
                                <p className='font-semibold'>{announcement.code}</p>
                                {announcement.endDate && (
                                    <>
                                        <p className='font-semibold'>
                                            End Date: {new Date(announcement.endDate).toLocaleString()}
                                        </p>
                                        <p className='font-semibold'>
                                            Time left: {countdown[announcement.id]?.days}d {countdown[announcement.id]?.hours}h {countdown[announcement.id]?.minutes}m {countdown[announcement.id]?.seconds}s
                                        </p>
                                    </>
                                )}
                                <p className='font-semibold'>Forward Link: {announcement.forward_link}</p>
                                <p className='font-semibold'>Forward Text: {announcement.forward_text}</p>
                            </div>
                            <button onClick={() => handleUnpinAnnouncement(announcement.id)}>
                                <PinOffIcon />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
            <div className='mb-8 p-8 rounded-xl bg-white border-black/15 shadow'>
                <h1 className='font-bold text-2xl mb-8'>Change Current Announcement</h1>
                <Selectbox
                    title="Select Announcement"
                    selectOptions={announcementOptions}
                    placeholder="Choose an announcement"
                    selected={selectedAnnouncement}
                    setSelected={setSelectedAnnouncement}
                />
                <div className="mt-4 flex items-center justify-between">
                    {selectedAnnouncement && (
                        <>
                            <div>
                                <h2 className="text-xl font-semibold">{selectedAnnouncement.name}</h2>
                                <p>{selectedAnnouncement.code}</p>
                            </div>
                            <div className='flex gap-2'>
                                <Button onClick={handlePinAnnouncement} className='w-72 !h-[50px] ml-auto' variant='primary'>
                                    Pin Announcement <PinIcon />
                                </Button>
                                <button onClick={() => setIsOpen(true)} className="text-[#EF3826]"><TrashIcon className='size-7' /></button>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <div className='p-8 rounded-xl bg-white border-black/15 shadow'>
                <h1 className='font-bold text-2xl mb-8'>Add New Announcement</h1>
                <form onSubmit={formik.handleSubmit} className='space-y-4'>
                    <div className='gap-4 flex'>
                        <Input value={formik.values.title}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                                formik.touched.title && formik.errors.title
                                    ? formik.errors.title
                                    : ""
                            }
                            className='w-full' name='title' title='Title' inputStyles='!h-[50px]' />
                        <Input value={formik.values.deadline}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className='w-full' name='deadline' title='Deadline' placeholder='00:00:00 (Optional)' inputStyles='!h-[50px]' />
                    </div>
                    <Input value={formik.values.message}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                            formik.touched.message && formik.errors.message
                                ? formik.errors.message
                                : ""
                        }
                        className='w-full' name='message' title='Message' inputStyles='!h-[50px]' />
                    <div className='gap-4 flex'>
                        <Input value={formik.values.forward_link}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className='w-full' name='forward_link' title='Forward Link' inputStyles='!h-[50px]' />
                        <Input value={formik.values.forward_text}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className='w-full' name='forward_text' title='Forward Text' inputStyles='!h-[50px]' />
                    </div>

                    <Button disabled={formik.isSubmitting} className='w-64 !h-[50px] ml-auto' variant='primary'>Add Announcement</Button>
                </form>
            </div>
        </div>
    );
}

export default Announcement;
